import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import DraftDocumentPreview from "../../documentManagement/components/DraftDocumentPreview";
import { useDraftDocumentPreview } from "../../documentManagement/hooks/useDraftDocumentPreview";
import { toBillingSubmitPayload } from "../../../_components/billing/toSubmitShape";
import { usePreviewUpsellMutation } from "../api/upsellApiSlice";

/**
 * Live "what will actually get sent" preview of the wizard's current draft -
 * renders the org's own configured Upsell document template (Settings ->
 * Document Templates), through the exact same backend pipeline
 * (documentRenderBridge.previewUpsellHtml, reusing createUpsell's own
 * repricing/proration engine - see upsellordownsell.controller.js's
 * previewUpsellDraft) a real create uses. This deliberately replaced an
 * earlier hand-built approximation (letterhead/items/totals assembled from
 * raw form state) that could never match a template an org actually
 * customized - see this file's git history for that version, and
 * ProposalPreviewPane.jsx's own docstring for the same rationale.
 *
 * `selectedSubscription` isn't RHF-backed (separate useState owned by
 * useUpsellWizard) - arrives as a plain prop exactly as UpsellReviewStep
 * already receives it; `market`/`subscription id` for the draft payload come
 * from it, same as useUpsellWizard's own onSubmit.
 */
const UpsellPreviewPane = ({ control, pricing, selectedSubscription }) => {
  const company = useWatch({ control, name: "company" });
  const billingCycle = useWatch({ control, name: "billingCycle" });
  const proposalTerms = useWatch({ control, name: "proposalTerms" });

  const [previewUpsell] = usePreviewUpsellMutation();

  const market = selectedSubscription?.marketId || selectedSubscription?.market;
  const subscriptionId = selectedSubscription?._id || selectedSubscription?.subscriptionId;

  // Mirrors previewUpsellDraft's own 400 guards - no point firing a request
  // the backend will always reject. Same currency gap as Proposal/Invoice's
  // own preview panes (see their docstrings) - a market with no currency
  // configured still has an `_id`.
  const hasMarket = Boolean(market?._id);
  const marketMissingCurrency = hasMarket && !market?.currency?.[0];
  const canPreview =
    Boolean(company?._id) && hasMarket && !marketMissingCurrency && Boolean(subscriptionId);

  const draftPayload = useMemo(() => {
    if (!canPreview) return null;
    return {
      company,
      market,
      subscription: subscriptionId,
      billingCycle,
      proposalTerms,
      // The same post-discount, post-tax total the real submit sends. Without it the server falls
      // back to the catalog subtotal, so every preview charged the discount and tax as if new.
      orderAmount: pricing.totals.totalAmount.toFixed(2),
      ...toBillingSubmitPayload(pricing, { selectedBillingCycle: billingCycle }),
    };
  }, [canPreview, company, market, subscriptionId, billingCycle, proposalTerms, pricing]);

  const { html, status, message } = useDraftDocumentPreview({
    canPreview,
    draftPayload,
    previewMutation: previewUpsell,
    notConfiguredMessage: "No published Upsell template is configured for this organization yet.",
    errorMessage: "Couldn't render the upsell preview. Try again shortly.",
  });

  return (
    <DraftDocumentPreview
      status={status}
      html={html}
      message={message}
      idleMessage={
        marketMissingCurrency
          ? `"${market.marketName}" has no currency configured - add one in Settings > Markets to preview this upsell.`
          : "Select a subscription and company to preview the upsell document."
      }
    />
  );
};

export default UpsellPreviewPane;
